import type { TaxBracket, TaxRebate, TaxThresholds, TaxYear } from '../../types/tax';

// Strategy interface for tax year calculations
export interface TaxYearStrategy {
  getYear(): TaxYear;
  getDisplayName(): string;
  getTaxBrackets(): TaxBracket[];
  getTaxRebates(): TaxRebate;
  getTaxThresholds(): TaxThresholds;
  getMedicalAidCredits(): {
    mainMember: number;
    firstDependent: number;
    additionalDependent: number;
  };
  getStartDate(): Date;
  getEndDate(): Date;
}

// Base abstract class for common functionality
export abstract class BaseTaxYearStrategy implements TaxYearStrategy {
  abstract getYear(): TaxYear;
  abstract getDisplayName(): string;
  abstract getTaxBrackets(): TaxBracket[];
  abstract getTaxRebates(): TaxRebate;
  abstract getTaxThresholds(): TaxThresholds;
  abstract getMedicalAidCredits(): { mainMember: number; firstDependent: number; additionalDependent: number; };
  abstract getStartDate(): Date;
  abstract getEndDate(): Date;
  
  // Common utility methods
  getTotalRebate(ageCategory: 'under65' | 'age65to74' | 'age75plus'): number {
    const rebates = this.getTaxRebates();
    switch (ageCategory) {
      case 'under65':
        return rebates.primary;
      case 'age65to74':
        return rebates.primary + rebates.secondary;
      case 'age75plus':
        return rebates.primary + rebates.secondary + rebates.tertiary;
      default:
        throw new Error('Invalid age category');
    }
  }
  
  getMedicalAidCredit(mainMember: boolean, dependents: number): number {
    if (!mainMember && dependents === 0) return 0;
    
    const credits = this.getMedicalAidCredits();
    let total = 0;
    
    if (mainMember) {
      total += credits.mainMember;
    }
    
    if (dependents > 0) {
      total += credits.firstDependent;
      if (dependents > 1) {
        total += (dependents - 1) * credits.additionalDependent;
      }
    }
    
    return total;
  }
  
  isCurrentYear(): boolean {
    const now = new Date();
    return now >= this.getStartDate() && now <= this.getEndDate();
  }
}